import { chunks, creatorKey } from '@discovr/features/discovery';
import type {
  DiscoveryPick,
  DiscoveryPicksPatch,
  DiscoveryResultCard,
  DiscoveryVettingVerdict,
} from '@discovr/types';
import { type ARRIVAL_SORT, RESULT_TABS, SORTS } from '../constants/findOptions';
import { FIT_SCALE, PICKS_PATCH_MAX } from '../constants/findLimits';
import { cardStats, type StatsCard } from './reelMetrics';

export type ResultTab = (typeof RESULT_TABS)[number]['tab'];
export type SortKey = (typeof SORTS)[number]['value'];
export type SortShown = SortKey | typeof ARRIVAL_SORT;

const BUCKET_TAB: Record<DiscoveryVettingVerdict['bucket'], ResultTab> = {
  qualified: 'passed',
  review: 'review',
  rejected: 'rejected',
};

export function tabOf(
  card: Pick<DiscoveryResultCard, 'vettingVerdict'>,
  pick: DiscoveryPick | undefined,
): ResultTab {
  if (pick?.placed) return pick.placed;
  return BUCKET_TAB[card.vettingVerdict?.bucket ?? 'qualified'];
}

export type PicksTarget = Pick<DiscoveryPicksPatch, 'platform' | 'platformIds'>;

export function picksBatches(
  creators: readonly Pick<DiscoveryResultCard, 'platform' | 'platformId'>[],
): PicksTarget[] {
  const platforms = [...new Set(creators.map((creator) => creator.platform))];
  return platforms.flatMap((platform) =>
    chunks(
      creators
        .filter((creator) => creator.platform === platform)
        .map((creator) => creator.platformId),
      PICKS_PATCH_MAX,
    ).map((platformIds) => ({ platform, platformIds })),
  );
}

export const fit100 = (fit: number | null) => (fit === null ? null : Math.round(fit * FIT_SCALE));

const UNKNOWN_LAST = -1;

export type SortableCard = StatsCard & Pick<DiscoveryResultCard, 'signals' | 'fit'>;

const SORTERS: Record<SortKey, (card: SortableCard) => number> = {
  persona: (card) => card.signals?.persona ?? UNKNOWN_LAST,
  fit: (card) => card.fit ?? 0,
  engagement: (card) => cardStats(card).engagement ?? UNKNOWN_LAST,
  reach: (card) => cardStats(card).reach ?? UNKNOWN_LAST,
  followers: (card) => card.followers,
};

export function sortCards<T extends SortableCard>(cards: readonly T[], key: SortKey): T[] {
  return cards
    .map((card) => ({ card, by: SORTERS[key](card), fit: SORTERS.fit(card) }))
    .sort((a, b) => b.by - a.by || b.fit - a.fit)
    .map(({ card }) => card);
}

export function matchesText(
  values: readonly (string | null | undefined)[],
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  return !needle || values.some((value) => value?.toLowerCase().includes(needle));
}

export const matchesCreator = (
  creator: { name: string | null; handle: string | null },
  query: string,
) => matchesText([creator.name, creator.handle], query);

export const matchesCard = (card: DiscoveryResultCard, query: string) =>
  matchesText(
    [card.name, card.handle, card.labels?.language, ...(card.labels?.niche_tags ?? [])],
    query,
  );

export const matchedPersonas = (card: DiscoveryResultCard) =>
  card.vettingVerdict?.persona?.matched ?? [];

export const personaLabelOf = (card: DiscoveryResultCard): string | null =>
  matchedPersonas(card)[0]?.trim() || null;

export type PickMap = ReadonlyMap<string, DiscoveryPick>;

export const pickMap = (picks: readonly DiscoveryPick[] | undefined): PickMap =>
  new Map((picks ?? []).map((pick) => [creatorKey(pick), pick]));

export const RESTORE_PLACEMENT = 'passed';

export function withRejected<Card extends Pick<DiscoveryResultCard, 'platform' | 'platformId'>>(
  scored: readonly Card[],
  rejected: readonly Card[],
): Card[] {
  const seen = new Set(scored.map(creatorKey));
  return [...scored, ...rejected.filter((card) => !seen.has(creatorKey(card)))];
}

export function tabCounts(
  cards: readonly Pick<DiscoveryResultCard, 'platform' | 'platformId' | 'vettingVerdict'>[],
  picks: PickMap,
): Record<ResultTab, number> {
  const counts: Record<ResultTab, number> = { passed: 0, review: 0, rejected: 0 };
  for (const card of cards) counts[tabOf(card, picks.get(creatorKey(card)))] += 1;
  return counts;
}
